import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/auth";

export default function Admin() {
 const { user } = useAuth();
 const [stats, setStats] = useState(null);
 const [balances, setBalances] = useState(null);
 const [users, setUsers] = useState([]);
 const [txs, setTxs] = useState([]);
 const [feedback, setFeedback] = useState([]);
 const [fbStats, setFbStats] = useState(null);
 const [tab, setTab] = useState("dashboard");
 const [err, setErr] = useState("");

 const loadFeedback = () => {
 Promise.all([
 axios.get(`${API}/admin/feedback`),
 axios.get(`${API}/admin/feedback/stats`),
 ]).then(([f, s]) => { setFeedback(f.data); setFbStats(s.data); })
 .catch(() => {});
 };

 useEffect(() => {
 if (!user || user.role !== "admin") return;
 Promise.all([
 axios.get(`${API}/admin/dashboard`),
 axios.get(`${API}/admin/users`),
 axios.get(`${API}/admin/transactions`),
 ]).then(([s, u, t]) => { setStats(s.data); setUsers(u.data); setTxs(t.data); })
 .catch(e => setErr(e.response?.data?.detail || "Admin load failed"));
 loadFeedback();
 // Provider balances refresh on mount + every 90s so ops sees credits
 // dropping BEFORE Suno silently falls back to TTS mid-battle.
 const loadBal = () => axios.get(`${API}/admin/provider-balances`)
 .then(r => setBalances(r.data)).catch(() => {});
 loadBal();
 const bIv = setInterval(loadBal, 90000);
 return () => clearInterval(bIv);
 }, [user]);

 const setFbStatus = async (id, status) => {
 try {
 await axios.patch(`${API}/admin/feedback/${id}`, { status });
 loadFeedback();
 } catch (e) { setErr(e.response?.data?.detail || "Update failed"); }
 };

 if (!user) return <div className="p-10 text-center"><Link to="/login" className="text-yt-yellow underline">Sign in</Link></div>;
 if (user.role !== "admin") return <div className="p-10 text-center font-mono text-yt-red">ADMIN ONLY</div>;

 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 font-mono">
 <div className="text-xs tracking-[0.4em] text-yt-judge mb-2"> COMMAND CENTER</div>
 <h1 className="font-display text-6xl tracking-tight mb-2">ADMIN</h1>
 <p className="text-yt-muted text-xs mb-8">{user.email}</p>

 <div className="flex gap-3 mb-8 border-b border-yt-border">
 {["dashboard", "users", "transactions", "feedback"].map(t => {
 const badge = t === "feedback" && fbStats?.new > 0 ? fbStats.new : null;
 return (
 <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
 className={`px-4 py-3 text-xs tracking-widest uppercase transition-colors relative ${tab === t ? "text-yt-yellow border-b-2 border-yt-yellow" : "text-yt-muted hover:text-white"}`}>
 {t}
 {badge && <span data-testid="feedback-new-badge" className="ml-2 inline-block bg-yt-red text-white px-1.5 py-0.5 rounded-sm text-[9px]">{badge}</span>}
 </button>
 );
 })}
 </div>

 {err && <div className="text-yt-red text-xs p-3 border border-yt-red/40 mb-6">{err}</div>}

 {tab === "dashboard" && stats && (
 <>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
 <Metric label="USERS" value={stats.total_users} testId="metric-users" />
 <Metric label="PAID" value={stats.paid_users} accent="text-yt-judge" testId="metric-paid" />
 <Metric label="BATTLES" value={stats.battles_total} testId="metric-battles" />
 <Metric label="TODAY" value={stats.battles_today} testId="metric-today" />
 </div>
 <div className="grid grid-cols-2 gap-3 mb-6">
 <Metric label="REVENUE TOTAL" value={`$${stats.revenue_total}`} accent="text-yt-yellow" big testId="metric-revenue" />
 <Metric label="MRR" value={`$${stats.mrr}`} accent="text-yt-yellow" big testId="metric-mrr" />
 </div>
 <div className="bg-[#0A0A0A] border-l-4 border-yt-judge p-6 scanlines">
 <div className="text-xs tracking-widest text-yt-judge mb-2">[ AI STATUS ]</div>
 <div className="text-yt-judge text-sm">{">"} PROVIDER: {stats.ai_provider}</div>
 <div className="text-yt-judge text-sm">{">"} STATUS: <span className="text-yt-judge">●</span> {stats.ai_status.toUpperCase()}</div>
 <div className="text-yt-muted text-xs mt-3">{">"} Top up Emergent LLM key from your Emergent profile → Universal Key → Add Balance.</div>
 </div>

 {/* ============================================================
 PROVIDER CREDIT MONITOR — early-warning so we notice BEFORE
 Suno / Runway fall back to TTS / static portraits mid-battle.
 ============================================================ */}
 <div className="mt-6" data-testid="provider-balances">
 <div className="text-xs tracking-[0.3em] text-yt-muted mb-3">PROVIDER CREDITS</div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <ProviderCard
 name="SUNO"
 subtitle="Rap audio (V4.5)"
 data={balances?.suno}
 testId="balance-suno"
 warn="Empty = battles fall back to TTS voice-over"
 />
 <ProviderCard
 name="RUNWAY"
 subtitle="Motion loops (Gen-4 Turbo)"
 data={balances?.runway}
 testId="balance-runway"
 warn="Empty = new characters render as static PNGs"
 />
 </div>
 </div>
 </>
 )}

 {tab === "users" && (
 <div className="border border-yt-border bg-yt-surface overflow-x-auto">
 <table className="w-full text-xs">
 <thead className="bg-black border-b border-yt-border text-yt-muted">
 <tr>
 <th className="text-left p-3">RAPPER</th>
 <th className="text-left p-3">EMAIL</th>
 <th className="text-left p-3">TIER</th>
 <th className="text-right p-3">BATTLES LEFT</th>
 <th className="text-right p-3">W</th>
 <th className="text-right p-3">L</th>
 <th className="text-right p-3">PTS</th>
 </tr>
 </thead>
 <tbody>
 {users.map(u => (
 <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-b border-yt-border">
 <td className="p-3"><span className="font-display text-yt-yellow mr-2">{(u.rapper_name || "??").slice(0, 2).toUpperCase()}</span>{u.rapper_name}</td>
 <td className="p-3 text-yt-muted">{u.email}</td>
 <td className="p-3 text-yt-yellow">{u.tier?.toUpperCase()}</td>
 <td className="p-3 text-right">{u.battles_remaining}</td>
 <td className="p-3 text-right text-yt-judge">{u.wins}</td>
 <td className="p-3 text-right text-yt-red">{u.losses}</td>
 <td className="p-3 text-right">{u.monthly_points}</td>
 </tr>
 ))}
 {users.length === 0 && <tr><td colSpan={7} className="text-center p-6 text-yt-muted">No users yet</td></tr>}
 </tbody>
 </table>
 </div>
 )}

 {tab === "transactions" && (
 <div className="border border-yt-border bg-yt-surface overflow-x-auto">
 <table className="w-full text-xs">
 <thead className="bg-black border-b border-yt-border text-yt-muted">
 <tr>
 <th className="text-left p-3">SESSION</th>
 <th className="text-left p-3">USER ID</th>
 <th className="text-left p-3">TIER</th>
 <th className="text-right p-3">AMOUNT</th>
 <th className="text-left p-3">STATUS</th>
 <th className="text-left p-3">CREATED</th>
 </tr>
 </thead>
 <tbody>
 {txs.map(t => (
 <tr key={t._id} className="border-b border-yt-border">
 <td className="p-3 truncate max-w-[160px]">{t.session_id}</td>
 <td className="p-3 truncate max-w-[160px] text-yt-muted">{t.user_id}</td>
 <td className="p-3 text-yt-yellow">{t.tier_key}</td>
 <td className="p-3 text-right">${t.amount}</td>
 <td className={`p-3 ${t.payment_status === "paid" ? "text-yt-judge" : "text-yt-muted"}`}>{t.payment_status?.toUpperCase()}</td>
 <td className="p-3 text-yt-muted">{new Date(t.created_at).toLocaleString()}</td>
 </tr>
 ))}
 {txs.length === 0 && <tr><td colSpan={6} className="text-center p-6 text-yt-muted">No transactions yet</td></tr>}
 </tbody>
 </table>
 </div>
 )}

 {tab === "feedback" && (
 <div data-testid="feedback-tab">
 {fbStats && (
 <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
 <Metric label="NEW" value={fbStats.new || 0} accent="text-yt-red" testId="fb-metric-new" />
 <Metric label="REVIEWING" value={fbStats.reviewing || 0} accent="text-yt-yellow" testId="fb-metric-reviewing" />
 <Metric label="RESOLVED" value={fbStats.resolved || 0} accent="text-yt-judge" testId="fb-metric-resolved" />
 <Metric label="TOTAL" value={fbStats.total || 0} testId="fb-metric-total" />
 </div>
 )}
 <div className="space-y-3">
 {feedback.length === 0 && (
 <div className="border border-yt-border bg-yt-surface p-8 text-center text-yt-muted text-xs">
 No feedback yet — the widget is live on every page.
 </div>
 )}
 {feedback.map(f => {
 const catColor = f.category === "bug" ? "text-yt-red" :
 f.category === "idea" ? "text-yt-yellow" :
 f.category === "love" ? "text-yt-judge" : "text-white";
 const statusColor = f.status === "new" ? "border-yt-red text-yt-red" :
 f.status === "reviewing" ? "border-yt-yellow text-yt-yellow" :
 f.status === "resolved" ? "border-yt-judge text-yt-judge" :
 "border-yt-muted text-yt-muted";
 return (
 <div key={f.id} data-testid={`feedback-item-${f.id}`} className="border border-yt-border bg-yt-surface p-4">
 <div className="flex justify-between items-start gap-4 flex-wrap mb-2">
 <div className="flex gap-3 items-center flex-wrap">
 <span className={`font-display text-xl ${catColor}`}>{f.category?.toUpperCase()}</span>
 <span className={`inline-block px-2 py-0.5 border font-mono text-[10px] tracking-widest ${statusColor}`}>{f.status?.toUpperCase()}</span>
 <span className="text-yt-muted text-[10px]">
 {f.rapper_name || f.email || "anon"} · {new Date(f.created_at).toLocaleString()}
 </span>
 </div>
 <div className="flex gap-1">
 {f.status !== "reviewing" && (
 <button data-testid={`fb-review-${f.id}`} onClick={() => setFbStatus(f.id, "reviewing")}
 className="border border-yt-yellow text-yt-yellow px-2 py-1 text-[10px] tracking-widest hover:bg-yt-yellow hover:text-black">REVIEWING</button>
 )}
 {f.status !== "resolved" && (
 <button data-testid={`fb-resolve-${f.id}`} onClick={() => setFbStatus(f.id, "resolved")}
 className="border border-yt-judge text-yt-judge px-2 py-1 text-[10px] tracking-widest hover:bg-yt-judge hover:text-black">RESOLVE</button>
 )}
 {f.status !== "wont_fix" && (
 <button data-testid={`fb-dismiss-${f.id}`} onClick={() => setFbStatus(f.id, "wont_fix")}
 className="border border-yt-muted text-yt-muted px-2 py-1 text-[10px] tracking-widest hover:bg-yt-muted hover:text-black">DISMISS</button>
 )}
 </div>
 </div>
 <div className="whitespace-pre-wrap text-white text-sm leading-relaxed">{f.message}</div>
 <div className="mt-2 text-[10px] text-yt-muted">
 Page: <span className="text-yt-muted">{f.page || "—"}</span>
 {f.email && <> · Email: <span className="text-yt-yellow">{f.email}</span></>}
 </div>
 </div>
 );
 })}
 </div>
 </div>
 )}
 </div>
 );
}

function Metric({ label, value, accent = "text-white", big, testId }) {
 return (
 <div data-testid={testId} className="bg-yt-surface border border-yt-border p-4">
 <div className="text-[10px] tracking-[0.3em] text-yt-muted">{label}</div>
 <div className={`font-display tracking-tight mt-1 ${big ? "text-5xl" : "text-3xl"} ${accent}`}>{value}</div>
 </div>
 );
}

/* ============================================================
 ProviderCard — one row per external provider showing credit
 balance, colour-coded status, and a top-up link when low/empty.
 Colours: ok=judge-green, low=yellow, empty/error=red.
 ============================================================ */
function ProviderCard({ name, subtitle, data, testId, warn }) {
 const status = data?.status || "unknown";
 const configured = data?.configured;
 const credits = data?.credits;
 const err = data?.error;
 const topup = data?.topup_url;

 const statusMeta = {
 ok: { border: "border-yt-judge", bg: "bg-yt-judge/10", color: "text-yt-judge", label: "● HEALTHY" },
 low: { border: "border-yt-yellow", bg: "bg-yt-yellow/10", color: "text-yt-yellow", label: "▲ LOW" },
 empty: { border: "border-yt-red", bg: "bg-yt-red/15", color: "text-yt-red", label: "✕ EMPTY" },
 error: { border: "border-yt-red", bg: "bg-yt-red/10", color: "text-yt-red", label: "! ERROR" },
 unknown: { border: "border-yt-border", bg: "bg-yt-surface", color: "text-yt-muted", label: "— UNKNOWN" },
 }[status];

 if (!configured) {
 return (
 <div data-testid={testId} className="border border-yt-border bg-yt-surface p-4">
 <div className="flex items-center justify-between">
 <div>
 <div className="font-display text-2xl">{name}</div>
 <div className="text-[10px] text-yt-muted">{subtitle}</div>
 </div>
 <div className="font-mono text-[10px] tracking-widest text-yt-muted">NOT CONFIGURED</div>
 </div>
 </div>
 );
 }

 return (
 <div data-testid={testId} className={`border-2 ${statusMeta.border} ${statusMeta.bg} p-4`}>
 <div className="flex items-start justify-between gap-2">
 <div>
 <div className="font-display text-2xl">{name}</div>
 <div className="text-[10px] text-yt-muted">{subtitle}</div>
 </div>
 <div className={`font-mono text-[10px] tracking-widest ${statusMeta.color}`}>{statusMeta.label}</div>
 </div>
 <div className="mt-3 flex items-end justify-between">
 <div>
 <div className={`font-display text-4xl ${statusMeta.color}`} data-testid={`${testId}-credits`}>
 {credits ?? "—"}
 </div>
 <div className="text-[9px] text-yt-muted tracking-widest">CREDITS REMAINING</div>
 </div>
 {topup && (status === "low" || status === "empty") && (
 <a href={topup} target="_blank" rel="noopener noreferrer"
 data-testid={`${testId}-topup`}
 className="bg-yt-yellow text-black font-mono text-[10px] tracking-widest px-3 py-2 hover:bg-white shadow-brutal">
 TOP UP ▶
 </a>
 )}
 </div>
 {err && <div className="mt-2 font-mono text-[9px] text-yt-red">{err}</div>}
 {warn && (status === "low" || status === "empty") && (
 <div className="mt-2 font-mono text-[10px] text-yt-muted italic">{warn}</div>
 )}
 </div>
 );
}
